import { useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ArrowRight, Database, Eye, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { oauthConsentService, type OAuthConsentDetails } from '@/services/oauthConsentService';
import { handleApiError } from '@/services/api';
import { stashIntendedRoute } from '@/utils/intendedRoute';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import type { AiApp } from '@/services/mcpConnectionsService';
import zaminoEmblem from '@/assets/zamino-emblem.png';

const APP_LABELS: Record<AiApp, string> = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  claude_code: 'Claude Code',
  other: 'External AI',
};

function appLabel(details: OAuthConsentDetails): string {
  if (details.app !== 'other') return APP_LABELS[details.app];
  return details.clientName || 'This app';
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="auth-surface">
      <div className="auth-bg-glow" aria-hidden="true" />
      <div className="auth-frame">
        <div className="auth-brand">
          <img src={zaminoEmblem} alt="Zamino" />
        </div>
        {children}
      </div>
    </div>
  );
}

function InvalidRequest() {
  return (
    <Shell>
      <h1 className="auth-headline">
        Request <span className="accent">expired.</span>
      </h1>
      <p className="auth-sub">
        This authorization request is no longer valid — it may have expired or already been
        decided. Return to your AI app and connect to Zamino again.
      </p>
      <p className="auth-micro" style={{ marginTop: 24 }}>
        <Link to="/home" className="auth-link">
          Go to Zamino
        </Link>
      </p>
    </Shell>
  );
}

/**
 * OAuth consent — the browser half of an external AI app's /authorize request
 * (docs/MCP_SELF_HOSTED_OAUTH_PLAN.md). Not wrapped in ProtectedRoute: an
 * unauthenticated user is bounced to /login with this URL stashed, so sign-in
 * returns them here to finish consenting.
 */
export default function OAuthConsentPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request_id');
  const [leaving, setLeaving] = useState(false);

  const detailsQuery = useQuery({
    queryKey: ['oauthConsent', requestId],
    queryFn: () => oauthConsentService.getDetails(requestId!),
    enabled: isAuthenticated && !!requestId,
    retry: (failureCount, error) =>
      failureCount < 2 && !(error instanceof AxiosError && error.response?.status === 404),
  });

  const decideMutation = useMutation({
    mutationFn: (action: 'approve' | 'deny') => oauthConsentService.decide(requestId!, action),
    onSuccess: ({ redirect_url }) => {
      setLeaving(true);
      window.location.assign(redirect_url);
    },
  });

  if (isLoading) {
    return (
      <Shell>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <LoadingSpinner size="lg" />
        </div>
      </Shell>
    );
  }

  if (!isAuthenticated) {
    stashIntendedRoute(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  if (!requestId) return <InvalidRequest />;

  if (detailsQuery.isLoading) {
    return (
      <Shell>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <LoadingSpinner size="lg" />
        </div>
      </Shell>
    );
  }

  if (detailsQuery.isError || !detailsQuery.data) return <InvalidRequest />;

  const details = detailsQuery.data;
  const app = appLabel(details);

  if (leaving) {
    return (
      <Shell>
        <h1 className="auth-headline">
          Returning to <span className="accent">{app}.</span>
        </h1>
        <p className="auth-sub">Finishing the connection…</p>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="auth-headline">
        Connect <span className="accent">{app}.</span>
      </h1>
      <p className="auth-sub">
        {app} is asking to connect to your Zamino account. If you approve, it will be able to:
      </p>

      <ul style={{ marginTop: 24, display: 'grid', gap: 14, listStyle: 'none', padding: 0 }}>
        <li style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Database size={18} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
          <span className="auth-help-text" style={{ margin: 0 }}>
            Read business data from the sources your account has been granted access to
          </span>
        </li>
        <li style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Eye size={18} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
          <span className="auth-help-text" style={{ margin: 0 }}>
            Ask read-only questions — it cannot change or delete anything
          </span>
        </li>
        <li style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <ShieldCheck size={18} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
          <span className="auth-help-text" style={{ margin: 0 }}>
            Be revoked anytime in Settings → External AI Access
          </span>
        </li>
      </ul>

      {details.canApprove ? (
        <>
          {decideMutation.isError && (
            <p className="auth-error-text" role="alert" style={{ marginTop: 20 }}>
              {handleApiError(decideMutation.error) || 'Could not record your decision. Try again.'}
            </p>
          )}
          <button
            type="button"
            className="auth-pill auth-pill-primary"
            style={{ marginTop: 28 }}
            disabled={decideMutation.isPending}
            onClick={() => decideMutation.mutate('approve')}
          >
            {decideMutation.isPending && decideMutation.variables === 'approve'
              ? 'Approving…'
              : `Allow ${app} to connect`}
            {!decideMutation.isPending && <ArrowRight size={14} strokeWidth={2.5} />}
          </button>
          <button
            type="button"
            className="auth-pill auth-pill-ghost"
            style={{ marginTop: 12 }}
            disabled={decideMutation.isPending}
            onClick={() => decideMutation.mutate('deny')}
          >
            {decideMutation.isPending && decideMutation.variables === 'deny' ? 'Denying…' : 'Deny'}
          </button>
        </>
      ) : (
        <p className="auth-help-text" style={{ marginTop: 28 }}>
          Your role in this workspace doesn't allow connecting external AI apps. Ask a workspace
          admin to approve this connection, or switch to a workspace where you have permission.
        </p>
      )}
    </Shell>
  );
}
